import { useState, useEffect } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconButton, Tooltip } from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import GenericFormPopup from "../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../api/apiVariable";
import { UserDto } from "./UsersTable";

export const fetchResetCode = async (userName: string) => {
  const { data } = await axios.post<{ resetCode: string }>(
    `${apiVariable}/forgotPassword`,
    JSON.stringify({userName}),
    { headers: { "Content-Type": "application/json" } }
  );
  return data.resetCode;
};

const ResetPasswordPopup: React.FC<{ user: UserDto }> = ({ user }) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [resetCode, setResetCode] = useState<string>();
  const [snackbar, setSnackbar] = useState<{
    status: "idle" | "success" | "error";
    message?: string;
  }>({ status: "idle" });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const code = await fetchResetCode(user.username);
        if (!code) throw new Error("Could not obtain reset code");
        setResetCode(code);
      } catch (e: any) {
        setSnackbar({
          status: "error",
          message: e.message || "Failed to start reset flow",
        });
        setOpen(false);
      }
    })();
  }, [open, user.username]);

  const mutation = useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      await axios.post(`${apiVariable}/resetPassword`, {
        userName: user.username,
        resetCode,
        newPassword: password,
      });
    },
    onSuccess: () => {
      setSnackbar({ status: "success", message: "Password reset!" });
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err: any) => {
      const data = err.response?.data;
      const msg = Array.isArray(data)
        ? data.map((e: any) => e.Description || e.description).join(" • ")
        : data?.Error || data || "Reset failed";
      setSnackbar({ status: "error", message: msg });
    },
  });

  const handleSubmit = (vals: { password: string; confirm: string }) => {
    if (vals.password !== vals.confirm) {
      setSnackbar({ status: "error", message: "Passwords don’t match" });
      return;
    }
    mutation.mutate({ password: vals.password });
  };

  return (
    <>
      <Tooltip title="Reset password">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <LockResetIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <GenericFormPopup
        open={open}
        title={`Reset password for “${user.username}”`}
        initialValues={{ password: "", confirm: "" }}
        formFields={[
          { name: "password", label: "New password", type: "password" },
          { name: "confirm", label: "Confirm password", type: "password" },
        ]}
        onSubmit={handleSubmit}
        onClose={() => setOpen(false)}
        loading={mutation.isPending || (!resetCode && open)}
      />

      <StatusSnackbar
        status={snackbar.status}
        successMessage={snackbar.message || ""}
        errorMessage={snackbar.message || ""}
        handleClose={() => setSnackbar((s) => ({ ...s, status: "idle" }))}
      />
    </>
  );
};

export default ResetPasswordPopup;
